/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TabbedContent } from '../../reactutils/withTabbedScreen';
import { Job } from '../../services/playlist';
import { GlobalState } from '../actions';
import { usePathSegment } from '../SysAdminApp/CacheStatisticsComponent';
import { sysAdminJobPath } from '../SysAdminApp/sysAdminPathes';
import { relevantServerNames, ServerType } from '../System/systemConfig';
import {
    deleteScheduledJob,
    disableJobScheduler,
    enableJobScheduler,
    loadJobsAction,
    setShowCreateJobFormAction,
} from './actions';
import { JobComponent } from './Job';
import { JobActions } from './JobActions';
import JobCreate from './JobCreate';
import {
    jobManagementServerAvailableSelector,
    jobSchedulerStateSelector,
    scheduledJobsSelector,
    showJobCreateFormSelector,
} from './selectors';

export const JobManagement: React.FC<{}> = (props) => {
    const [serverName] = usePathSegment(3) as ServerType[];
    const jobs = useSelector((state: GlobalState) => scheduledJobsSelector(state, serverName)) || ([] as Job[]);
    const enabled = useSelector((state: GlobalState) => jobSchedulerStateSelector(state, serverName)) || false;
    const showCreateJobForm =
        useSelector((state: GlobalState) => showJobCreateFormSelector(state, serverName)) || false;
    const serverAvailable =
        useSelector((state: GlobalState) => jobManagementServerAvailableSelector(state, serverName)) || false;

    const dispatch = useDispatch();
    const onLoad = React.useCallback(() => {
        if (serverName) {
            dispatch(loadJobsAction(serverName as ServerType));
        }
    }, [serverName, dispatch]);

    const onDelete = (triggerId: string, triggerGroup: string) => {
        dispatch(
            deleteScheduledJob({
                serverName: serverName as ServerType,
                triggerId: triggerId,
                triggerGroup: triggerGroup,
            }),
        );
    };
    const onDisable = () => {
        dispatch(disableJobScheduler(serverName));
    };
    const onEnable = () => {
        dispatch(enableJobScheduler(serverName));
    };
    const onReload = () => {
        dispatch(loadJobsAction(serverName));
    };
    const onShowForm = () => {
        dispatch(setShowCreateJobFormAction({ serverName: serverName, show: true }));
    };

    React.useEffect(() => onLoad(), [onLoad]);

    if (!serverAvailable) {
        return (
            <div>
                <TabbedContent activeTab={serverName} tabLinks={sysAdminJobPath} tabNames={relevantServerNames()} />
                <p className="m-2">The server ist currently not available</p>
                <JobActions
                    isAvailable={serverAvailable}
                    isEnabled={enabled}
                    onDisable={onDisable}
                    onEnable={onEnable}
                    onReload={onReload}
                    onSchedule={onShowForm}
                />
            </div>
        );
    } else {
        return (
            <div>
                <TabbedContent activeTab={serverName} tabLinks={sysAdminJobPath} tabNames={relevantServerNames()} />
                <JobActions
                    isAvailable={serverAvailable}
                    isEnabled={enabled}
                    onDisable={onDisable}
                    onEnable={onEnable}
                    onReload={onReload}
                    onSchedule={onShowForm}
                />
                {showCreateJobForm && <JobCreate serverName={serverName} />}
                {!showCreateJobForm && (
                    <table className="table table-bordered table-striped">
                        <thead>
                            <tr>
                                <th>Job</th>
                                <th className="d-none d-lg-table-cell">Description</th>
                                <th>State</th>
                                <th className="d-none d-lg-table-cell">Next fire time</th>
                                <th className="text-nowrap d-none d-lg-table-cell">Cron mask</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs &&
                                jobs.map((job, index) => <JobComponent key={index} job={job} onDelete={onDelete} />)}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }
};
